import type { BusSearchPendingField, BusSearchStop, BusSearchValue } from '../models.js';
import type { PlaceSuggestion } from '../../shared/place_search/place_panel.js';

/** O teto de viajantes do QUEM — a mesma régua do aéreo (1..9): cada viajante
 *  escolhe uma poltrona em cada trecho. */
export const BUS_MAX_TRAVELERS = 9;

/** Os campos que faltam, na ordem da barra. */
export function pendingBusSearchFields(value: BusSearchValue): BusSearchPendingField[] {
  const pending: BusSearchPendingField[] = [];
  if (value.travelers.length === 0) pending.push('travelers');
  if (!value.from) pending.push('from');
  if (!value.to) pending.push('to');
  if (!value.outwardDate) pending.push('outwardDate');
  return pending;
}

/** O pedido não é uma viagem que exista: mesma origem e destino, ida no
 *  passado, ou volta antes da ida. `today` é o dia LOCAL (`YYYY-MM-DD`). */
export function invalidBusSearch(value: BusSearchValue, today: string): boolean {
  const sameStop = value.from !== undefined && value.from.code === value.to?.code;
  const pastOutward = value.outwardDate !== undefined && value.outwardDate < today;
  const returnBeforeOutward =
    value.returnDate !== undefined &&
    value.outwardDate !== undefined &&
    value.returnDate < value.outwardDate;
  return sameStop || pastOutward || returnBeforeOutward;
}

/** Marcar/desmarcar um viajante no QUEM. O último não sai (≥ 1) e ninguém
 *  entra além do teto — o picker já mostra o motivo na linha. */
export function toggleBusTraveler<P extends { readonly id: string }>(
  travelers: readonly P[],
  person: P,
): readonly P[] {
  if (travelers.some((traveler) => traveler.id === person.id)) {
    return travelers.length > 1
      ? travelers.filter((traveler) => traveler.id !== person.id)
      : travelers;
  }
  return travelers.length < BUS_MAX_TRAVELERS ? [...travelers, person] : travelers;
}

/** As datas confirmadas no Quando: a ida e, se houver, a volta. Confirmar só a
 *  ida APAGA a volta anterior — é a busca só de ida. */
export function withBusDates(
  value: BusSearchValue,
  range: { readonly from?: string; readonly to?: string },
): BusSearchValue {
  return {
    travelers: value.travelers,
    ...(value.policyTravelerId ? { policyTravelerId: value.policyTravelerId } : {}),
    ...(value.from ? { from: value.from } : {}),
    ...(value.to ? { to: value.to } : {}),
    ...(range.from ? { outwardDate: range.from } : {}),
    ...(range.to ? { returnDate: range.to } : {}),
  };
}

/** Quantas paradas recentes cada campo lista. */
const RECENT_STOPS = 5;

/** As paradas das buscas recentes, por ponta, sem repetir e na ordem do fio
 *  (a mais recente primeiro) — as linhas de relógio do campo aberto vazio
 *  (`recentStops`), na página e no card da conversa. */
export function busRecentStops(
  searches: readonly { readonly from: BusSearchStop; readonly to: BusSearchStop }[],
  groupLabel: string,
): { from: PlaceSuggestion[]; to: PlaceSuggestion[] } {
  const side = (stops: readonly BusSearchStop[]) =>
    stops
      .filter((stop, index) => stops.findIndex((other) => other.code === stop.code) === index)
      .slice(0, RECENT_STOPS)
      .map((stop) => ({
        id: stop.code,
        label: stop.name,
        icon: 'history' as const,
        groupLabel,
      }));
  return {
    from: side(searches.map((search) => search.from)),
    to: side(searches.map((search) => search.to)),
  };
}
